import {
  takeLatest,
  put,
  select,
  call,
  all,
  delay,
  take,
  takeEvery,
  fork,
  spawn,
  actionChannel,
} from 'redux-saga/effects';
import axios from 'axios';
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  where,
  query,
  setDoc,
  Timestamp,
  addDoc,
  getDocs,
  deleteDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { logEvent } from 'firebase/analytics';
import dayjs from 'dayjs';

import { db, storage, analytics } from 'firebaseConfig';

import {
  createDocumentChangeChannel,
  convertGSToDownloadURL,
  getUserPhotoURL,
  memoizer,
} from 'utils';
import { demoProjects } from 'utils/contentProvider';
import sendUserEvent from 'utils/sendUserEvent';

import { showSnackbar, hideSnackbar } from 'modules/App/store/actions';
import {
  billingDetailsRequest,
  pushNotification,
  signInSuccess,
  listenNotifications,
} from 'modules/Users/store/actions';
import { teamsRequest } from 'modules/Team/store/actions';

import { allTeams } from 'modules/Team/store/selectors';

import {
  actions,
  allProjectsRequest,
  allProjectsSuccess,
  projectSuccess,
  toggleCurrentProjectLoading,
  toggleAddProjectModal,
} from './actions';

import * as selectors from './selectors';

const getUser = memoizer(async (uid) => {
  const userSnap = await getDoc(doc(db, 'users', uid));

  if (userSnap.get('isDeleted') === true) {
    return {
      uid,
      email: userSnap.get('email'),
      isDeleted: true,
    };
  }

  return { uid, ...userSnap.data() };
});

function* delayAndHideSnackbar() {
  yield delay(5000);
  yield put(hideSnackbar());
}

function* updateProjectPagesKey(ids) {
  const pagesBatch = writeBatch(db);
  const newPages = yield all(
    ids.map((page) => getDoc(doc(db, 'projects', 'pages', page))),
  );

  newPages.forEach(({ docs }, index) => {
    pagesBatch.update(doc(db, 'projects', ids[index]), {
      pages: docs.map((i) => ({
        id: i.id,
        createdAt: i.get('createdAt'),
        url: i.get('url'),
        numberOfVersions: i.get('versions') ? i.get('versions').length : 0,
      })),
    });
  });

  yield pagesBatch.commit();
}

/**
 * Adds current logged-in user to the requested project in `location.pathname`.
 * @param {Array} projectsArray
 */

function* checkShareableProject(projectsArray) {
  const pathnameSplits = window.location.pathname.split('/');
  const returnArray = [...projectsArray];

  if (demoProjects.includes(pathnameSplits[2])) return projectsArray;

  if (
    pathnameSplits.length > 2 &&
    pathnameSplits[1] === 'share' &&
    projectsArray.some((i) => i.id === pathnameSplits[2]) === false
  ) {
    try {
      const queriedProjectSnap = yield getDoc(
        doc(db, 'projects', pathnameSplits[2]),
      );

      if (
        queriedProjectSnap.exists() === true &&
        (queriedProjectSnap.get('isShared') === true ||
          queriedProjectSnap.get('isShared').public === true)
      ) {
        const user = yield select(selectors.user);

        if (!queriedProjectSnap.get('users').some((u) => u.uid === user.uid)) {
          const newProjectUsers = [
            ...queriedProjectSnap.get('users'),
            { access: 'view', uid: user.uid },
          ];

          yield updateDoc(doc(db, 'projects', pathnameSplits[2]), {
            users: newProjectUsers,
          });
          returnArray.push({
            ...queriedProjectSnap.data(),
            id: queriedProjectSnap.id,
            users: newProjectUsers,
          });
        }
      }
    } catch (error) {
      // Do nothing
    }
  }

  return returnArray;
}

function* checkProjectsEditors(projects) {
  const user = yield select(selectors.user);
  const editors = Array.from(
    new Set(
      projects
        .filter((p) => p.owner === user.uid)
        .map((p) =>
          p.users.filter((u) => u.access === 'edit' && u.uid !== user.uid),
        )
        .flat()
        .map((u) => u.uid || u.email)
        .filter((u) => typeof u === 'string'),
    ),
  );

  if (
    !user.projectsEditors ||
    user.projectsEditors?.length !== editors.length
  ) {
    yield updateDoc(doc(db, 'users', user.uid), { projectsEditors: editors });
    yield put(signInSuccess({ projectsEditors: editors }));
    yield delay(1000);
    yield put(billingDetailsRequest());
  }
}

function* postProjectsSuccess() {
  const teams = yield select(allTeams);

  if (teams === null) {
    yield put(teamsRequest());
    yield put(listenNotifications());
  }
}

function* projectsChannel(changeSnap) {
  const user = yield select(selectors.user);
  let projectsArray = changeSnap.docs;
  let allUsers = [];

  if (projectsArray.length === 0) {
    yield put(allProjectsSuccess([]));
    yield call(postProjectsSuccess);
    return;
  }

  // yield call(checkProjectsEditors, projectsArray);

  projectsArray = projectsArray.map((p) => ({ ...p.data(), id: p.id }));
  projectsArray = yield call(checkShareableProject, projectsArray);

  // Prepare thumbail URLs and sort.

  projectsArray = projectsArray.map((project) => {
    if (
      (!project.thumbnail &&
        !project.thumbnail?.startsWith('data') &&
        !project.thumbnail?.startsWith('http')) ||
      project.thumbnail?.startsWith('thumbnails/')
    ) {
      if (project.thumbnail?.startsWith('thumbnails/'))
        project.thumbnail = convertGSToDownloadURL(project.thumbnail);
      /* eslint-disable indent */ else
        project.thumbnail =
          project.pages?.length > 0
            ? convertGSToDownloadURL(
                `thumbnails/${project.id}-${
                  project.pages.sort(
                    (a, b) => a.createdAt.valueOf() - b.createdAt.valueOf(),
                  )[0].id
                }.jpg`,
                project.modifiedAt ? String(project.modifiedAt.seconds) : false,
              )
            : null;
      /* eslint-enable indent */
    }

    project.users.forEach((u) => u.uid && allUsers.push(u.uid));

    return project;
  });

  // Get user data

  allUsers = Array.from(
    new Set(allUsers.filter((i) => i && i !== user.uid)),
  ).map((u) => getUser(u));
  allUsers = yield all(allUsers);
  allUsers = [user, ...allUsers];

  projectsArray = projectsArray.map((p) => {
    p.users = p.users.map((u) => {
      const projectUser = {
        ...u,
        ...allUsers.find(({ uid }) => uid === u.uid),
      };

      return {
        ...projectUser,
        ...(projectUser.photoURL
          ? { photoURL: getUserPhotoURL(projectUser.photoURL) }
          : {}),
      };
    });

    if (typeof p.isShared !== 'object')
      p.isShared = {
        public: p.isShared || false,
        canComment:
          typeof p.isShared === 'object' ? p.isShared.canComment : true,
        showAllPages:
          typeof p.isShared === 'object' ? p.isShared.showAllPages : true,
        showPageVersions:
          typeof p.isShared === 'object' ? p.isShared.showPageVersions : false,
        showEdits:
          typeof p.isShared === 'object' ? p.isShared.showEdits : false,
      };

    return p;
  });

  yield put(allProjectsSuccess(projectsArray));

  // Check projects editors.

  yield call(checkProjectsEditors, projectsArray);

  // Reset currentProject if exists.

  const currentProject = yield select(selectors.currentProject);

  if (currentProject?.id) {
    const newCurrentProject = projectsArray.find(
      ({ id }) => id === currentProject.id,
    );

    if (newCurrentProject) yield put(projectSuccess(newCurrentProject));
  }

  if (
    projectsArray.some(
      (i) => (i.type === 'web' || i.type === 'webapp') && !i.pages,
    )
  ) {
    const updatePages = [];

    projectsArray.forEach((i) => {
      if ((i.type === 'web' || i.type === 'webapp') && !i.pages)
        updatePages.push(i.id);
    });

    yield call(updateProjectPagesKey, updatePages);
  }

  yield call(postProjectsSuccess);
}

function* cancelProjectsChannel(channel) {
  yield take(actions.CANCEL_PROJECTS_LISTENER);
  channel.close();
}

function* allProjectsRequestSaga() {
  const user = yield select(selectors.user);

  if (!user || !user.uid) return;

  const conditions = [
    user.uid,
    { uid: user.uid, access: 'edit' },
    { uid: user.uid, access: 'comment' },
    { uid: user.uid, access: 'view' },
  ];
  const projectsColChannel = yield call(
    createDocumentChangeChannel,
    query(
      collection(db, 'projects'),
      where('users', 'array-contains-any', conditions),
      where('isArchived', '==', false),
    ),
  );

  yield takeEvery(projectsColChannel, projectsChannel);
  yield fork(cancelProjectsChannel, projectsColChannel);
}

export function* watchAllProjectsRequest() {
  yield takeLatest(actions.ALL_PROJECTS_REQUEST, allProjectsRequestSaga);
}

function* projectRequestSaga({ payload, navigate }) {
  let user = yield select(selectors.user);
  const projects = yield select(selectors.projects);
  let projectData = '';
  let newSlides = [];
  let users = null;

  yield put(toggleCurrentProjectLoading(true));

  if (!user.uid) {
    yield call(allProjectsRequest);
    user = yield select(selectors.user);
  }

  if (projects.some((p) => p.id === payload) === false) {
    projectData = yield getDoc(doc(db, 'projects', payload));

    if (projectData.exists() === false) {
      yield put(
        showSnackbar('Project not found. Redirecting to all projects.'),
      );
      navigate('/projects');
      yield spawn(delayAndHideSnackbar);
      return;
    }
    projectData = { id: projectData.id, ...projectData.data() };
  } else projectData = projects.find((p) => p.id === payload);

  if (projectData.users.some((i) => i.uid === user.uid) === false) {
    navigate('/projects');
    return;
  }

  // Check if `users` is an array of string or object.
  // Then modify the doc.users to object[uid, access]. Remove doc.usersAccess.
  // Old data consisted of doc.users[string] and doc.usersAccess[string] keys in db.
  if (projectData.users.some((i) => typeof i === 'string')) {
    users = projectData.users.map((u, i) => ({
      uid: u,
      access: (projectData.usersAccess && projectData.usersAccess[i]) || 'edit',
    }));
    const { id, ...oldProjectDoc } = projectData;
    if (oldProjectDoc.usersAccess) delete oldProjectDoc.usersAccess;
    oldProjectDoc.users = users;
    yield setDoc(doc(db, 'projects', payload), oldProjectDoc);
  }

  if (projectData.type === 'illustration') {
    newSlides = yield all(
      projectData.slides.map((s) => {
        if (s.thumbnail) {
          if (s.thumbnail.startsWith('data:image')) {
            return { ...s, thumbnail: s.thumbnail };
          }
          return getDownloadURL(ref(storage, s.thumbnail)).then((url) => ({
            ...s,
            thumbnail: url,
          }));
        }
        return s;
      }),
    );
  }
  yield put(
    projectSuccess({
      ...projectData,
      slides: newSlides,
      ...(users || {}),
    }),
  );

  yield put(toggleCurrentProjectLoading(false));
}

export function* watchProjectRequest() {
  yield takeLatest(actions.PROJECT_REQUEST, projectRequestSaga);
}

function* addNewProjectSaga({ payload, navigate }) {
  const timestamp = Timestamp.now();
  const user = yield select(selectors.user);
  const projects = yield select(selectors.projects);
  const isFreeUser = user.projectsCount === 0;
  let newProjectDocRef = '';
  let newSlideDocRef = {};
  let newSlide = {};
  const newProject = {
    ...payload,
    createdAt: timestamp,
    modifiedAt: timestamp,
    users: [{ uid: user.uid, access: 'edit' }],
    owner: user.uid,
    isArchived: false,
    ...(payload.type.startsWith('web') ? { pages: [] } : {}),
    ...(isFreeUser
      ? { isExpired: Timestamp.fromDate(dayjs().add(2, 'day').toDate()) }
      : {}),
  };

  newProjectDocRef = yield addDoc(collection(db, 'projects'), newProject);
  newProject.id = newProjectDocRef.id;

  if (payload.type === 'illustration') {
    newSlideDocRef = yield addDoc(collection(newProjectDocRef, 'slides'), {
      createdAt: timestamp,
    });
    newSlide = {
      id: newSlideDocRef.id,
      createdAt: timestamp,
      noOfComments: 0,
      thumbnail: null,
    };
    yield updateDoc(newProjectDocRef, {
      slides: [newSlide],
    });
    newProject.slides = [newSlide];
  }

  newProject.users = [{ ...user, access: 'edit' }];

  yield put(allProjectsSuccess([newProject, ...projects]));
  yield put(projectSuccess(newProject));
  yield put(toggleAddProjectModal(false));
  yield put(showSnackbar('New project created.'));
  logEvent(analytics, 'project_create', {
    event_user: (user && user.uid) || null,
    project: (newProject && newProject.id) || null,
  });

  navigate(`/project/${newProjectDocRef.id}`);

  if (payload.type === 'webapp' && process.env.REACT_APP_ENV === 'production')
    yield fork(
      sendUserEvent,
      `${user.email} created a web app project "${payload.title}".`,
    );

  yield spawn(delayAndHideSnackbar);
}

export function* watchAddNewProject() {
  yield takeLatest(actions.ADD_NEW_PROJECT, addNewProjectSaga);
}

function* updateShareProjectUsers(
  newUser,
  access,
  newUsers,
  currentProject,
  isNewUser,
  callback,
) {
  const projectSnapRef = doc(db, 'projects', currentProject.id);
  const user = yield select(selectors.user);

  newUsers.push({ ...newUser, access });

  callback(true);
  yield put(showSnackbar('Project shared with the user.'));
  if (currentProject.users)
    yield put(
      projectSuccess({
        ...currentProject,
        users: newUsers,
      }),
    );
  newUsers = newUsers.map((i) => ({
    access: i.access,
    ...(i.uid ? { uid: i.uid } : { email: i.email }),
  }));
  yield updateDoc(projectSnapRef, {
    users: newUsers,
  });
  logEvent(analytics, 'project_user_add', {
    event_user: (user && user.uid) || null,
    added_user: (newUser && newUser.uid) || null,
    project: (currentProject && currentProject.id) || null,
  });
  yield spawn(delayAndHideSnackbar);
  callback(false);

  // Push notification.

  if (newUser.uid)
    yield put(
      pushNotification({
        type: ['share'],
        context: {
          projectID: currentProject.id,
        },
        sharedWith: newUser.uid,
      }),
    );

  try {
    if (isNewUser)
      yield axios.post(
        `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/inviteUserEmail`,
        {
          from: user.displayName,
          toEmail: newUser.email,
          projectTitle: currentProject.title,
        },
      );
    else
      yield axios.post(
        `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`,
        {
          from: `Team ruttl <support@ruttl.com>`,
          to: newUser.email,
          subject: `${user.displayName} has added you to a ruttl project`,
          template: 'project-share-email',
          templateVariables: JSON.stringify({
            from: user.displayName,
            projectTitle: currentProject.title,
            projectLink: `${window.location.origin}/project/${currentProject.id}`,
          }),
        },
      );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
}

function* shareProjectSaga({ payload, callback }) {
  const projects = yield select(selectors.projects);
  const projectUsersColSnap = yield getDocs(
    query(collection(db, 'users'), where('email', '==', payload.email)),
  );
  let newUser = null;
  const newUsers = payload.currentProject.users
    ? [...payload.currentProject.users]
    : [...projects.find((i) => i.id === payload.currentProject.id).users];
  let isNewUser = false; // New user on ruttl, not project.

  if (
    projectUsersColSnap.empty ||
    (projectUsersColSnap.docs.length === 1 &&
      projectUsersColSnap.docs[0].get('isDeleted') === true)
  ) {
    isNewUser = true;
    newUser = { email: payload.email };
  } else if (projectUsersColSnap.docs.length > 1) {
    const activeAccounts = projectUsersColSnap.docs
      .map((i) => ({ uid: i.id, ...i.data() }))
      .filter((i) => !i.isDeleted);

    if (activeAccounts?.length > 0) [newUser] = activeAccounts;
  } else {
    [newUser] = projectUsersColSnap.docs;
    newUser = { uid: newUser.id, ...newUser.data() };
  }

  if (newUser)
    yield call(
      updateShareProjectUsers,
      newUser,
      payload.access,
      newUsers,
      payload.currentProject,
      isNewUser,
      callback,
    );
}

export function* watchShareProject() {
  yield takeLatest(actions.SHARE_PROJECT, shareProjectSaga);
}

function* deleteProjectSaga({ payload: { id } }) {
  const user = yield select(selectors.user);

  try {
    yield deleteDoc(doc(db, 'projects', id));
    yield put(showSnackbar('Project deleted.'));
    logEvent(analytics, 'project_delete', {
      event_user: (user && user.uid) || null,
      project: id || null,
    });
    yield spawn(delayAndHideSnackbar);
  } catch (error) {
    /* Handle errors */
  }
}

export function* watchDeleteProject() {
  const deleteProjectChannel = yield actionChannel(actions.DELETE_PROJECT);

  while (true) {
    const payload = yield take(deleteProjectChannel);

    yield call(deleteProjectSaga, payload);
  }
}

function* updateProjectTitleSaga({ payload: { id, title } }) {
  const projects = yield select(selectors.projects);
  const user = yield select(selectors.user);

  try {
    let updatedProjects = [];

    yield updateDoc(doc(db, 'projects', id), { title });
    updatedProjects = projects.map((p) => (p.id === id ? { ...p, title } : p));
    yield put(allProjectsSuccess(updatedProjects));
    logEvent(analytics, 'project_title_update', {
      event_user: (user && user.uid) || null,
      project: id || null,
    });
  } catch (error) {
    // Handle errors
  }
}

export function* watchUpdateProjectTitle() {
  yield takeLatest(actions.UPDATE_PROJECT_TITLE, updateProjectTitleSaga);
}

function* updateProjectThumbnailWorkerSaga({ payload: { id, thumbnail } }) {
  const projects = yield select(selectors.projects);
  const user = yield select(selectors.user);

  try {
    let updatedProjects = [];

    yield updateDoc(doc(db, 'projects', id), { thumbnail });
    updatedProjects = projects.map((p) =>
      p.id === id ? { ...p, thumbnail } : p,
    );
    yield put(allProjectsSuccess(updatedProjects));

    yield put(showSnackbar('Project thumbnail updated.'));

    yield spawn(delayAndHideSnackbar);

    logEvent(analytics, 'project_thumbnail_update', {
      event_user: (user && user.uid) || null,
      project: id || null,
    });
  } catch (error) {
    // Handle errors
  }
}

export function* updateProjectThumbnailWatcherSaga() {
  yield takeLatest(
    actions.UPDATE_PROJECT_THUMBNAIL,
    updateProjectThumbnailWorkerSaga,
  );
}

function* updateProjectUsersSaga({
  payload: { newUsers, currentProjectUsers },
}) {
  const currentProject = yield select(selectors.currentProject);
  const users = newUsers.map((i) => ({
    access: i.access === 'owner' ? 'edit' : i.access,
    ...(i.uid ? { uid: i.uid } : { email: i.email }),
  }));
  const user = yield select(selectors.user);
  const ownerUpdate = newUsers.some((u) => u.access === 'owner')
    ? { owner: newUsers.find((u) => u.access === 'owner').uid }
    : {};

  yield updateDoc(doc(db, 'projects', currentProject.id), {
    users,
    ...ownerUpdate,
  });

  if (!window.location.pathname.startsWith('/projects'))
    yield put(
      projectSuccess({
        ...currentProject,
        ...ownerUpdate,
        users: newUsers,
      }),
    );

  if (!users.some((u) => u.uid === user.uid)) {
    yield put(
      showSnackbar('You have successfully removed yourself from the project.'),
    );
    logEvent(analytics, 'project_user_remove_self', {
      event_user: (user && user.uid) || null,
      project: (currentProject && currentProject.id) || null,
    });
  } else if (users.length < currentProjectUsers.length) {
    yield put(showSnackbar('User removed from the project.'));
    logEvent(analytics, 'project_user_remove_user', {
      event_user: (user && user.uid) || null,
      project: (currentProject && currentProject.id) || null,
      removed_user:
        currentProjectUsers.filter(
          (i) => !users.some((u) => u.uid === i.uid),
        )[0].uid || null,
    });
  } else if (users.length === currentProjectUsers.length)
    yield put(showSnackbar('User access updated.'));

  yield spawn(delayAndHideSnackbar);
}

export function* updateProjectUsers() {
  yield takeLatest(actions.UPDATE_PROJECT_USERS, updateProjectUsersSaga);
}

function* updateProjectSharedSaga({ payload: { isShared, projectID } }) {
  const currentProject = yield select(selectors.currentProject);

  yield updateDoc(doc(db, 'projects', projectID), { isShared });
  yield put(projectSuccess({ ...currentProject, isShared }));
}

export function* watchUpdateProjectShared() {
  yield takeLatest(actions.UPDATE_PROJECT_SHARED, updateProjectSharedSaga);
}

function* updateProjectApproversWorkerSaga({
  payload: { id, currentProjectID },
}) {
  const user = yield select(selectors.user);
  const projects = yield select(selectors.projects);
  const currentProject = projects.find((p) => p.id === currentProjectID);

  if (!currentProject) return;

  const currentApprovers = currentProject.approvers || [];

  let newApprovers;

  const existingApprover = currentApprovers.includes(id);

  if (existingApprover) {
    newApprovers = currentApprovers.filter((c) => c !== id);
  } else {
    newApprovers = [...currentApprovers, id];
  }

  yield updateDoc(doc(db, 'projects', currentProjectID), {
    approvers: newApprovers,
  });

  yield put(
    projectSuccess({
      ...currentProject,
      approvers: newApprovers,
    }),
  );

  yield put(showSnackbar('Project approver updated.'));

  if (!existingApprover) {
    const approver = currentProject.users.find(({ uid }) => uid === id);

    yield axios.post(
      `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`,
      {
        from: `Team ruttl <support@ruttl.com>`,
        to: approver.email,
        subject: `You are now an approver on Ruttl’s project ‘${currentProject.title}’`,
        template: 'make-approver',
        templateVariables: JSON.stringify({
          project: currentProject.title,
          owner: user.displayName,
          url: `${window.location.origin}/project/${currentProjectID}`,
        }),
      },
    );

    yield put(
      pushNotification({
        type: 'make-approver',
        context: {
          projectID: currentProjectID,
          projectName: currentProject.title,
          approverID: approver.uid,
        },
      }),
    );
  }

  yield spawn(delayAndHideSnackbar);
}

export function* updateProjectApproversWatcherSaga() {
  yield takeLatest(
    actions.UPDATE_PROJECT_APPROVERS,
    updateProjectApproversWorkerSaga,
  );
}

function* updateTrelloBoard({ payload }) {
  const currentProject = yield select(selectors.currentProject);

  if (currentProject.trelloBoardID) {
    if (currentProject.trelloBoardID !== payload) {
      yield updateDoc(doc(db, 'projects', currentProject.id), {
        trelloBoardID: payload,
      });

      yield put(
        projectSuccess({
          ...currentProject,
          trelloBoardID: payload,
        }),
      );
    }
  } else {
    yield updateDoc(doc(db, 'projects', currentProject.id), {
      trelloBoardID: payload,
    });

    yield put(
      projectSuccess({
        ...currentProject,
        trelloBoardID: payload,
      }),
    );
  }
}

export function* updateTrelloBoardWatcherSaga() {
  yield takeLatest(actions.UPDATE_TRELLO_BOARD, updateTrelloBoard);
}

function* changeProjectsUserAccessSaga({ payload: { access, uid, projects } }) {
  const updateBatch = writeBatch(db);
  const allProjects = yield select(selectors.projects);
  const includedProjects = allProjects.filter(
    (p) => projects.includes(p.id) && p.users.some((u) => u.uid === uid),
  );

  includedProjects.forEach((p) => {
    updateBatch.update(doc(db, 'projects', p.id), {
      users: p.users.map((u) => ({
        uid: u.uid,
        access: u.uid === uid ? access : u.access,
      })),
    });
  });

  yield updateBatch.commit();

  yield put(showSnackbar('User access updated.'));
  yield spawn(delayAndHideSnackbar);
  yield delay(5000);
  yield put(billingDetailsRequest());
}

export function* watchChangeProjectsUserAccess() {
  yield takeLatest(
    actions.CHANGE_PROJECTS_USER_ACCESS,
    changeProjectsUserAccessSaga,
  );
}
